import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { Briefcase, Globe, Lightbulb, Sparkles } from "lucide-react";
import VolunteerBadge from "../VolunteerBadge";

const ACTIVITY_LABELS = {
  hiking: { label: "Hiking", emoji: "🥾" },
  yoga: { label: "Yoga", emoji: "🧘" },
  cooking: { label: "Cooking", emoji: "🍳" },
  music: { label: "Music", emoji: "🎵" },
  art: { label: "Art", emoji: "🎨" },
  fitness: { label: "Fitness", emoji: "💪" },
  entrepreneurship: { label: "Building", emoji: "🚀" },
};

const ResidentSheet = ({ resident, open, onOpenChange }) => {
  if (!resident) return null;
  const activities = resident.activities || {};
  const interestList = Object.keys(ACTIVITY_LABELS).filter((k) => activities[k]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-3xl bg-[#FBF7F1] max-h-[88vh] overflow-y-auto">
        <SheetHeader className="text-left">
          <div className="flex items-start gap-4 pt-2">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-gradient-to-br from-[#C85A38] to-[#F2CC8F] flex-shrink-0 flex items-center justify-center text-white font-serif text-2xl ring-4 ring-white shadow-md">
              {resident.avatar ? (
                <img src={resident.avatar} alt={resident.name} className="w-full h-full object-cover" />
              ) : (
                resident.name?.charAt(0)
              )}
            </div>
            <div className="flex-1 min-w-0 pt-1">
              <SheetTitle className="font-serif text-2xl text-[#1A1A1A] leading-tight flex items-center gap-2 flex-wrap">
                <span>{resident.name}</span>
                <VolunteerBadge show={resident.is_volunteer} />
              </SheetTitle>
              <SheetDescription className="text-sm text-[#1A1A1A]/60 mt-0.5">
                {[resident.nationality, resident.occupation].filter(Boolean).join(" · ") || "Saying hi soon"}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="mt-5 space-y-4">
          {resident.current_project && (
            <div className="bg-white rounded-2xl p-4 border border-stone-100">
              <div className="flex items-center gap-2 text-[#C85A38] text-xs font-medium uppercase tracking-wide mb-1">
                <Lightbulb className="w-4 h-4" /> Currently working on
              </div>
              <p className="text-[#1A1A1A] text-sm leading-relaxed">{resident.current_project}</p>
            </div>
          )}

          {resident.fun_fact && (
            <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl p-4 border border-amber-100">
              <div className="flex items-center gap-2 text-amber-700 text-xs font-medium uppercase tracking-wide mb-1">
                <Sparkles className="w-4 h-4" /> Fun fact
              </div>
              <p className="text-[#1A1A1A] text-sm leading-relaxed italic">&ldquo;{resident.fun_fact}&rdquo;</p>
            </div>
          )}

          {interestList.length > 0 && (
            <div>
              <p className="text-[#1A1A1A]/60 text-xs font-medium uppercase tracking-wide mb-2">Into</p>
              <div className="flex flex-wrap gap-2">
                {interestList.map((k) => (
                  <span key={k} className="inline-flex items-center gap-1 bg-white rounded-full border border-stone-200 px-3 py-1 text-xs text-[#1A1A1A]">
                    <span>{ACTIVITY_LABELS[k].emoji}</span>
                    {ACTIVITY_LABELS[k].label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {resident.bringing_pet && resident.pet_name && (
            <div className="bg-[#8C9A7C]/10 rounded-2xl p-4 flex items-center gap-3 border border-[#8C9A7C]/20">
              <span className="text-3xl">{resident.pet_type === "cat" ? "🐱" : "🐕"}</span>
              <div>
                <p className="font-medium text-[#1A1A1A]">{resident.pet_name}</p>
                <p className="text-xs text-[#1A1A1A]/60">{resident.pet_breed || `${resident.pet_type === "cat" ? "Cat" : "Dog"}`} with {resident.name}</p>
              </div>
            </div>
          )}

          {!resident.current_project && !resident.fun_fact && interestList.length === 0 && (
            <div className="text-center py-6">
              <Briefcase className="w-10 h-10 text-[#1A1A1A]/20 mx-auto mb-2" />
              <p className="text-[#1A1A1A]/50 text-sm">{resident.name} hasn&apos;t filled in their profile yet.</p>
              <p className="text-[#1A1A1A]/40 text-xs mt-1">Give them a warm welcome when you meet them.</p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ResidentSheet;
